/**
 * Pincode Checker with Metaobject Integration
 * Handles pincode validation, local storage, and metaobject data fetching
 */

class PincodeChecker {
  constructor() {
    this.STORAGE_KEY = 'userPincode';
    this.PINCODE_DATA_KEY = 'pincodeData';
    this.CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    this.pincodeData = null;
    this.expressPincodeData = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.container = document.querySelector('[data-pincode-checker]');
    if (!this.container) return;

    this.elements = {
      dataContainer: this.container.querySelector('.pincode-data'),
      inputSection: this.container.querySelector('[data-input-section]'),
      resultSection: this.container.querySelector('[data-result-section]'),
      loadingSection: this.container.querySelector('[data-pincode-loading]'),
      input: this.container.querySelector('[data-pincode-input]'),
      submitBtn: this.container.querySelector('[data-pincode-submit]'),
      changeBtn: this.container.querySelector('[data-pincode-change]'),
      error: this.container.querySelector('[data-pincode-error]'),
      resultIcon: this.container.querySelector('[data-result-icon]'),
      resultLocation: this.container.querySelector('[data-result-location]'),
      resultWarehouse: this.container.querySelector('[data-result-warehouse]'),
      resultMessage: this.container.querySelector('[data-result-message]'),
      pincodeValue: this.container.querySelector('[data-pincode-value]')
    };

    this.config = {
      metaobjectId: this.elements.dataContainer.dataset.metaobjectId,
      productId: this.elements.dataContainer.dataset.productId,
      productInstantDelivery: this.elements.dataContainer.dataset.productInstantDelivery === 'true',
      productAvailable: this.elements.dataContainer.dataset.productAvailable === 'true',
      instantDeliveryMessage: this.elements.dataContainer.dataset.instantDeliveryMessage,
      standardDeliveryMessage: this.elements.dataContainer.dataset.standardDeliveryMessage,
      unavailableMessage: this.elements.dataContainer.dataset.unavailableMessage,
      notEligibleMessage: this.elements.dataContainer.dataset.notEligibleMessage,
      outOfStockMessage: this.elements.dataContainer.dataset.outOfStockMessage,
      enableTimeBased: this.elements.dataContainer.dataset.enableTimeBased === 'true',
      deliveryStartHour: parseInt(this.elements.dataContainer.dataset.deliveryStartHour) || 8,
      deliveryEndHour: parseInt(this.elements.dataContainer.dataset.deliveryEndHour) || 19,
      morningSlotEnd: parseInt(this.elements.dataContainer.dataset.morningSlotEnd) || 10,
      weekdayMorningEdd: this.elements.dataContainer.dataset.weekdayMorningEdd,
      weekdayDayEdd: this.elements.dataContainer.dataset.weekdayDayEdd,
      afterHoursEdd: this.elements.dataContainer.dataset.afterHoursEdd || 'Delivery in 24 hours',
      weekdayNightMessage: this.elements.dataContainer.dataset.weekdayNightMessage,
      weekendMessage: this.elements.dataContainer.dataset.weekendMessage,
      timeRestrictedMessage: this.elements.dataContainer.dataset.timeRestrictedMessage,
      enableWeekends: this.elements.dataContainer.dataset.enableWeekends === 'true',
      offDays: this.parseOffDays( this.elements.dataContainer.dataset.offDays )
    };

    this.attachEventListeners();
    this.checkStoredPincode();
  }

  attachEventListeners() {
    // Submit button click
    this.elements.submitBtn.addEventListener('click', () => this.handleSubmit());

    // Enter key on input
    this.elements.input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        this.handleSubmit();
      }
    });

    // Input validation - only numbers
    this.elements.input.addEventListener('input', (e) => {
      e.target.value = e.target.value.replace(/[^0-9]/g, '');
      this.hideError();
    });

    // Change pincode button
    this.elements.changeBtn.addEventListener('click', () => this.resetForm());
  }

  async handleSubmit() {
    const pincode = this.elements.input.value.trim();

    // Validate pincode
    if (!this.validatePincode(pincode)) {
      this.showError('Please enter a valid 6-digit pincode');
      return;
    }

    this.showLoading();
    
    try {
      // Fetch pincode data from metaobject
      await this.fetchPincodeData();
      
      // Check if pincode is available
      const result = this.checkPincodeAvailability(pincode);
      
      // Save to local storage
      this.savePincodeToStorage(pincode, result);
      
      // Display result
      this.displayResult(pincode, result);
    } catch (error) {
      console.error('Pincode check error:', error);
      this.showError('Unable to check pincode availability. Please try again.');
      this.hideLoading();
    }
  }

  validatePincode(pincode) {
    return /^\d{6}$/.test(pincode);
  }

  async fetchPincodeData() {
    // Check if we have cached data
    const cachedData = this.getCachedPincodeData();
    if (cachedData) {
      this.pincodeData = cachedData.instant || cachedData;
      this.expressPincodeData = cachedData.express || {};
      return;
    }

    // Use the pre-loaded data from Liquid template
    if (window.pincodeCheckerData && window.pincodeCheckerData.pincodes) {
      this.pincodeData = window.pincodeCheckerData.pincodes;
      this.expressPincodeData = window.pincodeCheckerData.expressPincodes || {};
      this.cachePincodeData({ instant: this.pincodeData, express: this.expressPincodeData });
      return;
    }

    // Fallback: Try to use cached data even if expired
    const expiredCache = this.getCachedPincodeData(true);
    if (expiredCache) {
      this.pincodeData = expiredCache.instant || expiredCache;
      this.expressPincodeData = expiredCache.express || {};
    } else {
      throw new Error('No pincode data available. Please configure the metaobject in theme settings.');
    }
  }

  parsePincodeData(metaobjectData) {
    // Parse the metaobject data structure
    // Adjust this based on your actual metaobject structure
    const pincodeMap = {};
    
    if (metaobjectData && metaobjectData.metaobject) {
      const fields = metaobjectData.metaobject.fields;
      
      // Assuming the metaobject has fields like:
      // - pincodes: list of pincodes
      // - locations: corresponding locations
      fields.forEach(field => {
        if (field.key === 'pincodes' && field.value) {
          const pincodes = JSON.parse(field.value);
          pincodes.forEach((item, index) => {
            pincodeMap[item.pincode] = {
              location: item.location || 'Available',
              available: true
            };
          });
        }
      });
    }
    
    return pincodeMap;
  }

  checkPincodeAvailability(pincode) {
    // First check if product is in stock
    if (!this.config.productAvailable) {
      return {
        available: false,
        location: null,
        message: this.config.outOfStockMessage,
        deliveryType: 'out_of_stock',
        eddTime: null,
        isProductIssue: true
      };
    }

    // Check time and day eligibility if enabled
    const timeCheck = this.config.enableTimeBased ? this.checkTimeEligibility() : { eligible: true, edd: null };

    // Check if pincode exists in metaobject data (Instant delivery areas)
    const isPincodeInMetaobject = this.pincodeData && this.pincodeData[pincode];
    // Check if pincode exists in express delivery metaobject
    const isPincodeInExpress = this.expressPincodeData && this.expressPincodeData[pincode];

    // Logic for delivery type:
    // 1. If pincode is in instant metaobject AND product has instant delivery enabled → Instant Delivery
    // 2. If pincode is in express metaobject → Express Delivery
    // 3. If pincode is NOT in either metaobject → Not Serviceable

    if (isPincodeInMetaobject && this.config.productInstantDelivery) {
      // Instant delivery area + Product eligible for instant delivery
      // But check if time is eligible
      if (!timeCheck.eligible) {
        // Time not eligible - show time restricted message
        return {
          available: true,
          location: this.pincodeData[pincode].location,
          warehouse: this.pincodeData[pincode].warehouse,
          message: timeCheck.message,
          deliveryType: 'standard',
          eddTime: null,
          isProductIssue: false,
          timeRestricted: true
        };
      }

      return {
        available: true,
        location: this.pincodeData[pincode].location,
        warehouse: this.pincodeData[pincode].warehouse,
        message: this.config.instantDeliveryMessage,
        deliveryType: 'instant',
        eddTime: timeCheck.edd,
        isProductIssue: false,
        timeRestricted: false
      };
    } else if (isPincodeInExpress) {
      // Express delivery area
      return {
        available: true,
        location: this.expressPincodeData[pincode].location || null,
        warehouse: this.expressPincodeData[pincode].warehouse || null,
        message: this.config.standardDeliveryMessage || 'Express delivery available for this pincode.',
        deliveryType: 'express',
        eddTime: null,
        isProductIssue: false,
        timeRestricted: false
      };
    } else {
      // Pincode NOT in instant or express → Not Serviceable
      return {
        available: false,
        location: null,
        message: this.config.unavailableMessage || 'Your pincode ' + pincode + ' is not serviceable.',
        deliveryType: 'not_serviceable',
        eddTime: null,
        isProductIssue: false,
        timeRestricted: false
      };
    }
  }

  parseOffDays( offDaysData ) {
    if ( !offDaysData || offDaysData === 'null' || offDaysData === 'undefined' ) return [];
    try {
      const parsed = JSON.parse( offDaysData );
      return Array.isArray( parsed ) ? parsed : [];
    } catch ( e ) {
      console.error( 'Error parsing off days:', e );
      return [];
    }
  }

  formatDate( date ) {
    const year = date.getFullYear();
    const month = String( date.getMonth() + 1 ).padStart( 2, '0' );
    const day = String( date.getDate() ).padStart( 2, '0' );
    return `${ year }-${ month }-${ day }`;
  }

  isOffDay( date ) {
    const offDays = this.config?.offDays;
    if ( !offDays || !Array.isArray( offDays ) || offDays.length === 0 ) return false;
    const dateStr = this.formatDate( date );
    return offDays.some( offDay => {
      if ( typeof offDay === 'string' ) return offDay === dateStr;
      if ( offDay && offDay.date ) return offDay.date === dateStr;
      return false;
    } );
  }

  checkTimeEligibility() {
    const now = new Date();
    const day = now.getDay();

    const istOffset = 5.5 * 60;
    const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000);
    const istDate = new Date(utcTime + (istOffset * 60000));
    const hour = istDate.getHours();
    const minute = istDate.getMinutes();
    const currentTime = hour + (minute / 60);

    if ( this.isOffDay( istDate ) ) {
      return {
        eligible: false,
        edd: this.config.afterHoursEdd || 'Delivery not available today',
        message: this.config.timeRestrictedMessage || 'Instant delivery is not available today (off day).'
      };
    }

    let startHour, endHour;

    if (day === 0) {
      startHour = 8;
      endHour = 17;
    } else {
      startHour = this.config.deliveryStartHour;
      endHour = this.config.deliveryEndHour;
    }

    if (currentTime >= startHour && currentTime < endHour) {
      return {
        eligible: true,
        message: null,
        edd: this.config.weekdayDayEdd
      };
    } else {
      return {
        eligible: true,
        message: null,
        edd: this.config.afterHoursEdd
      };
    }
  }

  displayResult(pincode, result) {
    this.hideLoading();
    this.elements.inputSection.style.display = 'none';
    this.elements.resultSection.style.display = 'block';

    // Set pincode value
    this.elements.pincodeValue.textContent = pincode;

    // Set icon based on delivery type
    let iconHTML = '';
    let iconClass = '';
    
    if (result.deliveryType === 'instant') {
      // Lightning bolt icon for instant delivery
      iconHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>';
      iconClass = 'success instant';
    } else if (result.deliveryType === 'express') {
      // Truck icon for express delivery
      iconHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg>';
      iconClass = 'success express';
    } else if (result.deliveryType === 'standard') {
      // Truck icon for standard/time-restricted delivery
      iconHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg>';
      iconClass = 'success standard';
    } else if (result.deliveryType === 'not_serviceable') {
      // X icon for not serviceable
      iconHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>';
      iconClass = 'error not-serviceable';
    } else {
      // X icon for out of stock
      iconHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
      iconClass = 'error';
    }
    
    this.elements.resultIcon.innerHTML = iconHTML;
    this.elements.resultIcon.className = `pincode-result-icon ${iconClass}`;

    // Set location
    if (result.location) {
      this.elements.resultLocation.textContent = result.location;
      this.elements.resultLocation.style.display = 'block';
    } else {
      this.elements.resultLocation.style.display = 'none';
    }

    // Set warehouse
    if (result.warehouse) {
      this.elements.resultWarehouse.innerHTML = '<span class="warehouse-label">📦 Warehouse:</span> <span class="warehouse-value">' + result.warehouse + '</span>';
      this.elements.resultWarehouse.style.display = 'block';
    } else {
      this.elements.resultWarehouse.style.display = 'none';
    }

    // Set message with delivery type badge and EDD
    let deliveryBadge = '';
    let eddDisplay = '';
    
    if (result.deliveryType === 'instant') {
      deliveryBadge = '<span class="delivery-badge instant">⚡ Instant Delivery</span>';
      if (result.eddTime) {
        eddDisplay = '<span class="edd-time">🕐 ' + result.eddTime + '</span>';
      }
    } else if (result.deliveryType === 'express') {
      deliveryBadge = '<span class="delivery-badge express">🚚 Express Delivery</span>';
    } else if (result.deliveryType === 'standard') {
      deliveryBadge = '<span class="delivery-badge standard">🚚 Standard Delivery</span>';
      if (result.timeRestricted) {
        eddDisplay = '<span class="time-info">⏰ ' + result.message + '</span>';
      }
    } else if (result.deliveryType === 'not_serviceable') {
      deliveryBadge = '<span class="delivery-badge not-serviceable">❌ Not Serviceable</span>';
    }

    const messageContent = result.timeRestricted ? '' : '<span>' + result.message + '</span>';

    const isError = result.deliveryType === 'out_of_stock' || result.deliveryType === 'not_serviceable';
    this.elements.resultMessage.innerHTML = deliveryBadge + eddDisplay + messageContent;
    this.elements.resultMessage.className = `pincode-result-message ${isError ? 'error' : 'success'} ${result.deliveryType}`;
  }

  resetForm() {
    this.elements.resultSection.style.display = 'none';
    this.elements.inputSection.style.display = 'block';
    this.elements.input.value = '';
    this.elements.input.focus();
  }

  checkStoredPincode() {
    const storedData = this.getStoredPincode();
    if (storedData && storedData.pincode) {
      // Auto-check the stored pincode
      this.elements.input.value = storedData.pincode;
      this.handleSubmit();
    }
  }

  savePincodeToStorage(pincode, result) {
    try {
      const data = {
        pincode: pincode,
        result: result,
        timestamp: Date.now()
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }

  getStoredPincode() {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      if (data) {
        const parsed = JSON.parse(data);
        // Check if data is not too old (24 hours)
        if (Date.now() - parsed.timestamp < this.CACHE_DURATION) {
          return parsed;
        }
      }
    } catch (error) {
      console.error('Error reading from localStorage:', error);
    }
    return null;
  }

  cachePincodeData(data) {
    try {
      const cacheData = {
        data: data,
        timestamp: Date.now()
      };
      localStorage.setItem(this.PINCODE_DATA_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error caching pincode data:', error);
    }
  }

  getCachedPincodeData(ignoreExpiry = false) {
    try {
      const cached = localStorage.getItem(this.PINCODE_DATA_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (ignoreExpiry || Date.now() - parsed.timestamp < this.CACHE_DURATION) {
          return parsed.data;
        }
      }
    } catch (error) {
      console.error('Error reading cached data:', error);
    }
    return null;
  }

  showLoading() {
    this.elements.inputSection.style.display = 'none';
    this.elements.loadingSection.style.display = 'flex';
  }

  hideLoading() {
    this.elements.loadingSection.style.display = 'none';
  }

  showError(message) {
    this.elements.error.textContent = message;
    this.elements.error.style.display = 'block';
  }

  hideError() {
    this.elements.error.style.display = 'none';
  }
}

// Initialize when script loads
new PincodeChecker();
